"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAllFieldGroups = getAllFieldGroups;
exports.getAllRepeatableFieldGroups = getAllRepeatableFieldGroups;
exports.getAllFields = getAllFields;
exports.getAllSections = getAllSections;
exports.getAllSubsections = getAllSubsections;
exports.getContainingSection = getContainingSection;
exports.findSection = findSection;
exports.findSubsectionInQuestionnaire = findSubsectionInQuestionnaire;
exports.findField = findField;
exports.findFieldInFieldGroup = findFieldInFieldGroup;
exports.sectionGroupHasSections = sectionGroupHasSections;
exports.getRepeatedFieldGroupIndex = getRepeatedFieldGroupIndex;
exports.getInitialFieldValue = getInitialFieldValue;
exports.getFieldValueType = getFieldValueType;
exports.getFieldTitlesByNodeId = getFieldTitlesByNodeId;
exports.getSubsectionInsuranceModules = getSubsectionInsuranceModules;
const lodash_1 = __importDefault(require("lodash"));
const fieldTypes_1 = require("../types/carrierQuestionnaire/fieldTypes");
const structure_1 = require("./structure");
function getAllFieldGroups(questionnaire) {
    const subsections = getAllSubsections(questionnaire);
    return lodash_1.default.flatMap(subsections, 'fieldGroups');
}
function getAllRepeatableFieldGroups(questionnaire) {
    return getAllFieldGroups(questionnaire).filter((fieldGroup) => fieldGroup.options?.repeatable);
}
function getAllFields(questionnaire) {
    const fieldGroups = getAllFieldGroups(questionnaire);
    return lodash_1.default.flatMap(fieldGroups, 'fields');
}
function getAllSections(questionnaire) {
    return lodash_1.default.flatMap(questionnaire, 'sections');
}
function getAllSubsections(questionnaire) {
    const sections = getAllSections(questionnaire);
    return lodash_1.default.flatMap(sections, 'subsections');
}
function getContainingSection(subsectionId, questionnaire) {
    const allSections = getAllSections(questionnaire);
    return allSections.find((section) => section.subsections.some(({ id }) => id === subsectionId));
}
function findSection(id, questionnaire) {
    return getAllSections(questionnaire).find((section) => section.id === id);
}
function findSubsection(id, section) {
    return section.subsections.find((subsection) => subsection.id === id);
}
function findSubsectionInQuestionnaire(id, questionnaire) {
    let subsection;
    questionnaire.some((sectionGroup) => sectionGroup.sections.some((section) => {
        subsection = findSubsection(id, section);
        return !!subsection;
    }));
    return subsection;
}
function findField(id, questionnaire) {
    const allFields = getAllFields(questionnaire);
    return allFields.find((field) => field.id === id);
}
function findFieldInFieldGroup(id, fieldGroup) {
    return fieldGroup.fields.find((field) => field.id === id);
}
function sectionGroupHasSections(sectionGroup) {
    return sectionGroup.sections.length > 0;
}
function getRepeatedFieldGroupIndex(fieldGroup) {
    return fieldGroup.metadata.repetitionIndex;
}
// Returns the initial 'blank' field value depending on the field value type
function getInitialFieldValue(field) {
    const answerType = getFieldValueType(field);
    switch (answerType) {
        case structure_1.FieldValueTypes.string:
            return '';
        case structure_1.FieldValueTypes.boolean:
            return false;
        case structure_1.FieldValueTypes.array:
            return [];
    }
}
// Returns the type of the value expected by the UI for a given field
// Note that this only represents the type used in the UI and is not indicative of the type of our persisted answers
// For example, a number field uses a text input (meaning the UI expects a string) but will be persisted as a number
function getFieldValueType(field) {
    switch (field.type) {
        case fieldTypes_1.FieldTypes.agree:
        case fieldTypes_1.FieldTypes.checkbox:
            return structure_1.FieldValueTypes.boolean;
        case fieldTypes_1.FieldTypes.checkboxGroup:
            return structure_1.FieldValueTypes.array;
        default:
            return structure_1.FieldValueTypes.string;
    }
}
function getFieldTitlesByNodeId(questionnaire, lang) {
    const fieldTitlesByNodeId = new Map();
    questionnaire.forEach((sectionGroup) => {
        sectionGroup.sections.forEach((section) => {
            section.subsections.forEach((subsection) => {
                subsection.fieldGroups.forEach((fieldGroup) => {
                    fieldGroup.fields.forEach((field) => {
                        if (!field.nodeId) {
                            return;
                        }
                        if (fieldTitlesByNodeId.has(field.nodeId)) {
                            return; // Already have a title for this nodeId.
                        }
                        let title = '';
                        if (field.title?.[lang]) {
                            title = field.title[lang];
                        }
                        else if (field.text?.[lang]) {
                            title = field.text[lang];
                        }
                        else if (fieldGroup.title?.[lang]) {
                            title = fieldGroup.title?.[lang];
                        }
                        else if (fieldGroup.text?.[lang]) {
                            title = fieldGroup.text?.[lang];
                        }
                        else {
                            // use the nodeId as the fallback as it will be easier to debug if we ever end up in that situation where
                            // field and fieldGroup do not have title/subtitle information.
                            title = field.nodeId;
                        }
                        fieldTitlesByNodeId.set(field.nodeId, title);
                    });
                });
            });
        });
    });
    return fieldTitlesByNodeId;
}
function getSubsectionInsuranceModules(subsectionId, questionnaire) {
    const parentSection = getContainingSection(subsectionId, questionnaire);
    return parentSection?.insuranceModuleFilter ?? [];
}
